import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Zap, QrCode, CreditCard, CheckCircle, Shield, Clock,
  Smartphone, ChevronRight, ArrowRight, Star, Menu, X,
  Building2, UtensilsCrossed, ShoppingBag, Car, Store, Tent,
  Users, BarChart3, Receipt, Bell, Globe, Lock, WifiOff,
  AlertTriangle, TrendingUp, Eye, Download, Palette, HeadphonesIcon,
  MonitorSmartphone, UserPlus, Play
} from 'lucide-react';

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/da75adbe-983a-4727-9109-6392f471d1c0/images/cbb163be207ffb630b07b4177223809eb0b339f7b158bba21f8dd5386d52209d.png';
const QR_COUNTER_IMG = 'https://static.prod-images.emergentagent.com/jobs/da75adbe-983a-4727-9109-6392f471d1c0/images/536b486e29302713cf09208c33173adbf965280c48194d75f5b8011da150957d.png';
const TEAM_IMG = 'https://static.prod-images.emergentagent.com/jobs/da75adbe-983a-4727-9109-6392f471d1c0/images/6113c76fdf3a5a269677c2308e5ee2af486a3f199421ae484c52b24585d9adb1.png';

/* ═══════════════════ TRANSLATIONS ═══════════════════ */
const i18n = {
  fr: {
    nav: { solution: 'Solution', features: 'Fonctionnalités', pricing: 'Tarifs', security: 'Sécurité', login: 'Connexion', start: 'Commencer' },
    hero: {
      badge: 'TPE en panne ? Internet coupé ?',
      h1a: "Continuez d'encaisser",
      h1b: 'par QR code.',
      desc: "EmergencyPOS transforme n'importe quel smartphone en terminal de paiement. Vos clients scannent, paient par carte, Apple Pay ou Google Pay. Vous encaissez. En toute sécurité, via Stripe.",
      cta: 'Créer mon compte gratuit',
      demo: 'Voir la démo',
      noSub: 'Sans abonnement', noCommit: 'Sans engagement', noHw: 'Sans matériel',
      confirmLabel: 'Paiement reçu',
    },
    stats: { uptime: 'Disponibilité', speed: 'Temps de paiement', setup: "Frais d'installation", secure: 'Paiements sécurisés' },
    problem: {
      label: 'Le problème',
      h2: "Quand le terminal tombe, votre chiffre d'affaires aussi",
      desc: "Pannes de TPE, coupures internet, mises à jour bancaires... Chaque minute sans encaissement est une vente perdue.",
      items: [
        { title: 'Internet en panne', desc: "Votre TPE dépend d'internet. Quand la connexion tombe, impossible d'encaisser par carte.", stat: '67%', statLabel: 'des commerçants ont déjà perdu des ventes à cause d\'une panne' },
        { title: 'TPE hors service', desc: "Panne matérielle, batterie vide, mise à jour bloquante... Le TPE n'est pas infaillible.", stat: '2-5 jours', statLabel: "délai moyen de remplacement d'un TPE" },
        { title: 'Clients sans espèces', desc: 'De plus en plus de clients paient uniquement par carte ou mobile. Pas de terminal = pas de vente.', stat: '82%', statLabel: 'des paiements en France sont dématérialisés' },
      ],
    },
    solution: {
      label: 'La solution',
      h2: 'Votre plan B pour les paiements, en 3 secondes',
      desc: 'Saisissez le montant, affichez un QR code, le client scanne et paie. C\'est tout.',
    },
    mockup: { amount: 'Saisie du montant', charge: 'ENCAISSER', qr: 'QR Code généré', qrAmount: 'Montant', qrScan: 'Le client scanne pour payer', confirm: 'Confirmation', paid: 'Paiement reçu' },
    qrModes: {
      label: 'Deux modes de paiement',
      h2: 'QR dynamique ou QR permanent : à vous de choisir',
      desc: 'Deux approches complémentaires pour encaisser par QR code, adaptées à tous les contextes.',
      dynamic: { title: 'QR Dynamique', sub: 'Par transaction', desc: 'Pour chaque encaissement, saisissez le montant sur le clavier et un QR code unique est généré instantanément. Le client le scanne avec son smartphone, choisit son moyen de paiement (carte, Apple Pay, Google Pay) et règle en quelques secondes. Vous recevez la confirmation en temps réel.', features: ['Un QR unique par transaction', 'Montant prédéfini, aucune erreur', 'Compatible Apple Pay, Google Pay, CB', 'Confirmation sonore instantanée'], idealFor: 'Idéal pour', cases: ['Pharmacies', 'Boutiques', 'Restaurants'] },
      permanent: { title: 'QR Permanent', sub: 'Par caissier / point de vente', badge: 'Nouveau', desc: "Chaque membre de votre équipe dispose d'un QR code fixe, imprimable et permanent. Placez-le sur le comptoir, en vitrine ou sur un chevalet. Le client le scanne, saisit lui-même le montant et paie. Chaque paiement est automatiquement attribué au bon caissier.", features: ['QR fixe par caissier, imprimable', 'Le client saisit le montant lui-même', 'Attribution automatique au bon caissier', 'Utilisable en mode autonome (self-checkout)'], idealFor: 'Idéal pour', cases: ['Marchés', 'Food trucks', 'VTC/Taxis', 'Pop-up stores'] },
      imgTitle: 'Un QR code sur votre comptoir, des paiements en continu',
      imgP1: "Imprimez le QR code de chaque caissier et placez-le à côté de la caisse, en vitrine, ou sur une table. Le client scanne à tout moment, saisit le montant et paie. Aucun appareil à manipuler côté commerçant.",
      imgP2: "Chaque paiement est tracé et attribué au bon membre de l'équipe. L'owner a une vue complète sur l'ensemble des transactions, tous caissiers confondus.",
    },
    platform: {
      label: 'Plateforme complète',
      h2: "Bien plus qu'un terminal de secours",
      desc: "EmergencyPOS est une plateforme complète de gestion des paiements, avec tableau de bord, gestion d'équipe et analytique intégrée.",
      allFeatures: 'Toutes les fonctionnalités',
      featureList: ['Clavier POS', 'QR dynamique', 'QR permanent', 'Apple Pay / Google Pay', "Gestion d'équipe", 'Analytique', 'Reçus personnalisés', 'Notifications email', 'Export CSV', 'Bilingue FR/EN', 'Branding personnalisé', 'Support intégré', 'Mode client', 'Rapports mensuels', 'Responsive mobile'],
    },
    mockupDash: { title: 'Tableau de bord', today: 'CA Jour', txns: 'Transactions', avg: 'Panier moyen' },
    mockupTxns: { title: 'Transactions récentes' },
    mockupStaff: { title: 'Équipe' },
    team: {
      label: "Gestion d'équipe",
      h2: 'Ajoutez des caissiers, gardez le contrôle',
      desc: "En tant que propriétaire, vous avez une vue complète sur l'activité de votre commerce. Ajoutez autant de caissiers que nécessaire, chacun avec son propre accès et son QR code permanent.",
      items: [
        { title: 'Ajout instantané', desc: "Créez un compte caissier en 10 secondes. Email, mot de passe, c'est parti." },
        { title: 'QR code individuel', desc: 'Chaque caissier a son QR fixe. Les paiements sont automatiquement attribués.' },
        { title: 'Supervision complète', desc: "L'owner voit toutes les transactions, tous les caissiers, en temps réel." },
        { title: 'Rôles et permissions', desc: 'Owner, Admin, Caissier — chaque rôle a ses propres permissions et sa vue dédiée.' },
      ],
    },
    howItWorks: {
      label: 'Comment ça marche',
      h2: 'Opérationnel en 5 minutes',
      steps: [
        { title: 'Créez votre compte', desc: 'Inscription gratuite en 30 secondes. Aucune carte bancaire requise.' },
        { title: 'Connectez Stripe', desc: 'Liez votre compte Stripe en un clic. Les fonds vont directement chez vous.' },
        { title: 'Générez un QR', desc: 'Saisissez le montant ou utilisez un QR permanent. Le client scanne et paie.' },
        { title: 'Encaissez', desc: "Confirmation instantanée avec alerte sonore. L'argent arrive sur votre compte." },
      ],
    },
    useCases: {
      label: "Cas d'usage",
      h2: 'Adapté à tous les commerces',
      desc: "Que vous soyez pharmacien, restaurateur ou vendeur sur les marchés, EmergencyPOS s'adapte à votre activité.",
      items: [
        { label: 'Pharmacies', desc: 'Le TPE tombe souvent en panne aux heures de pointe' },
        { label: 'Restaurants', desc: 'QR permanent sur chaque table' },
        { label: 'Boutiques', desc: 'Solution de secours toujours prête' },
        { label: 'Marchés', desc: "QR permanent sans besoin d'internet" },
        { label: 'VTC / Taxis', desc: 'QR dans le véhicule, paiement immédiat' },
        { label: 'Pop-up shops', desc: 'Encaissez sans aucun matériel' },
      ],
    },
    pricing: {
      label: 'Tarification',
      h2: 'Payez uniquement quand vous encaissez',
      desc: "Pas d'abonnement. Pas de frais d'installation. Pas d'engagement. Juste une commission sur chaque transaction réussie.",
      per: 'par transaction réussie',
      min: 'minimum 0.10 EUR par transaction',
      features: ['Transactions illimitées', 'Comptes caissiers illimités', 'Historique complet', 'QR dynamiques + permanents', 'Apple Pay & Google Pay', 'Confirmations en temps réel', 'Mode affichage client', 'Tableau de bord analytique', 'Reçus email personnalisés', 'Branding personnalisé', 'Notifications par email', 'Export CSV'],
      cta: 'Créer mon compte gratuit',
      noCc: "Aucune carte bancaire requise pour l'inscription",
      stripeTitle: 'Frais Stripe',
      stripeDesc: "Stripe applique ses propres frais de traitement (généralement 1.5% + 0.25 EUR pour les cartes européennes). Ces frais sont facturés directement par Stripe et sont indépendants des frais EmergencyPOS.",
      zeroSetup: 'Installation', zeroMonthly: 'Abonnement', zeroCommit: 'Engagement', none: 'Aucun',
    },
    security: {
      label: 'Sécurité',
      h2: "La sécurité d'un grand du paiement",
      items: [
        { title: 'PCI DSS Niveau 1', desc: 'Tous les paiements transitent par Stripe, certifié PCI DSS Niveau 1. Aucune donnée bancaire ne touche nos serveurs.' },
        { title: 'Fonds directs', desc: "Grâce à Stripe Connect, l'argent va directement du client à votre compte bancaire. EmergencyPOS ne détient jamais vos fonds." },
        { title: 'Traçabilité complète', desc: 'Chaque transaction est horodatée, attribuée au bon caissier, avec notification en temps réel et historique complet exportable.' },
      ],
    },
    cta: {
      h2: 'Ne perdez plus jamais une vente',
      desc: "Configurez EmergencyPOS en 5 minutes. Aucun matériel nécessaire. Prêt à encaisser dès aujourd'hui.",
      btn: 'Créer mon compte gratuit',
      loginBtn: 'Se connecter',
      sub: 'Inscription gratuite · Sans carte bancaire · Sans engagement',
    },
    footer: {
      desc: "La solution de paiement QR d'urgence pour les commerces. Acceptez les paiements même quand tout tombe en panne.",
      product: 'Produit', securityF: 'Sécurité', account: 'Compte',
      pci: 'PCI DSS', stripeConnect: 'Stripe Connect', dataProtection: 'Protection des données',
      loginF: 'Connexion', registerF: 'Inscription',
      copyright: 'Tous droits réservés.',
      poweredBy: 'Propulsé par Stripe',
    },
  },
  en: {
    nav: { solution: 'Solution', features: 'Features', pricing: 'Pricing', security: 'Security', login: 'Login', start: 'Get started' },
    hero: {
      badge: 'Terminal down? Internet out?',
      h1a: 'Keep accepting payments',
      h1b: 'via QR code.',
      desc: 'EmergencyPOS turns any smartphone into a payment terminal. Your customers scan, pay by card, Apple Pay or Google Pay. You get paid. Securely, via Stripe.',
      cta: 'Create my free account',
      demo: 'See the demo',
      noSub: 'No subscription', noCommit: 'No commitment', noHw: 'No hardware',
      confirmLabel: 'Payment received',
    },
    stats: { uptime: 'Uptime', speed: 'Payment time', setup: 'Setup fees', secure: 'Secure payments' },
    problem: {
      label: 'The problem',
      h2: 'When your terminal goes down, so does your revenue',
      desc: 'Terminal failures, internet outages, bank updates... Every minute without payments is a lost sale.',
      items: [
        { title: 'Internet is down', desc: 'Your terminal depends on the internet. When the connection drops, card payments stop.', stat: '67%', statLabel: 'of merchants have lost sales due to outages' },
        { title: 'Terminal failure', desc: "Hardware failure, dead battery, blocking updates... Your terminal isn't infallible.", stat: '2-5 days', statLabel: 'average time to replace a terminal' },
        { title: 'Cashless customers', desc: 'More and more customers pay only by card or mobile. No terminal = no sale.', stat: '82%', statLabel: 'of payments in Europe are cashless' },
      ],
    },
    solution: {
      label: 'The solution',
      h2: 'Your payment backup plan, in 3 seconds',
      desc: "Enter the amount, display a QR code, the customer scans and pays. That's it.",
    },
    mockup: { amount: 'Amount entry', charge: 'CHARGE', qr: 'QR Code generated', qrAmount: 'Amount', qrScan: 'Customer scans to pay', confirm: 'Confirmation', paid: 'Payment received' },
    qrModes: {
      label: 'Two payment modes',
      h2: 'Dynamic QR or permanent QR: your choice',
      desc: 'Two complementary approaches for QR code payments, adapted to all contexts.',
      dynamic: { title: 'Dynamic QR', sub: 'Per transaction', desc: 'For each payment, enter the amount on the keypad and a unique QR code is instantly generated. The customer scans it with their smartphone, chooses their payment method (card, Apple Pay, Google Pay) and pays in seconds. You receive real-time confirmation.', features: ['Unique QR per transaction', 'Pre-set amount, zero errors', 'Apple Pay, Google Pay, card compatible', 'Instant sound confirmation'], idealFor: 'Ideal for', cases: ['Pharmacies', 'Retail stores', 'Restaurants'] },
      permanent: { title: 'Permanent QR', sub: 'Per cashier / point of sale', badge: 'New', desc: 'Each team member has a fixed, printable, permanent QR code. Place it on the counter, in the window, or on a stand. The customer scans it, enters the amount themselves and pays. Each payment is automatically assigned to the right cashier.', features: ['Fixed QR per cashier, printable', 'Customer enters amount themselves', 'Auto-assigned to right cashier', 'Self-checkout mode capable'], idealFor: 'Ideal for', cases: ['Markets', 'Food trucks', 'Ride-hailing/Taxis', 'Pop-up stores'] },
      imgTitle: 'A QR code on your counter, continuous payments',
      imgP1: "Print each cashier's QR code and place it next to the register, in the window, or on a table. Customers scan anytime, enter the amount and pay. No device handling needed on the merchant side.",
      imgP2: 'Every payment is tracked and assigned to the right team member. The owner has a complete view of all transactions across all cashiers.',
    },
    platform: {
      label: 'Complete platform',
      h2: 'Much more than a backup terminal',
      desc: 'EmergencyPOS is a complete payment management platform with dashboard, team management and built-in analytics.',
      allFeatures: 'All features',
      featureList: ['POS Keypad', 'Dynamic QR', 'Permanent QR', 'Apple Pay / Google Pay', 'Team management', 'Analytics', 'Custom receipts', 'Email notifications', 'CSV export', 'Bilingual FR/EN', 'Custom branding', 'Built-in support', 'Customer display', 'Monthly reports', 'Mobile responsive'],
    },
    mockupDash: { title: 'Dashboard', today: 'Today Rev.', txns: 'Transactions', avg: 'Avg Basket' },
    mockupTxns: { title: 'Recent transactions' },
    mockupStaff: { title: 'Team' },
    team: {
      label: 'Team management',
      h2: 'Add cashiers, keep control',
      desc: 'As the owner, you have a complete view of your business activity. Add as many cashiers as needed, each with their own access and permanent QR code.',
      items: [
        { title: 'Instant setup', desc: 'Create a cashier account in 10 seconds. Email, password, done.' },
        { title: 'Individual QR code', desc: 'Each cashier has their fixed QR. Payments are auto-assigned.' },
        { title: 'Full oversight', desc: 'The owner sees all transactions, all cashiers, in real time.' },
        { title: 'Roles & permissions', desc: 'Owner, Admin, Cashier — each role has its own permissions and dedicated view.' },
      ],
    },
    howItWorks: {
      label: 'How it works',
      h2: 'Up and running in 5 minutes',
      steps: [
        { title: 'Create your account', desc: 'Free sign-up in 30 seconds. No credit card required.' },
        { title: 'Connect Stripe', desc: 'Link your Stripe account in one click. Funds go directly to you.' },
        { title: 'Generate a QR', desc: 'Enter the amount or use a permanent QR. Customer scans and pays.' },
        { title: 'Get paid', desc: 'Instant confirmation with sound alert. Money arrives in your account.' },
      ],
    },
    useCases: {
      label: 'Use cases',
      h2: 'Built for any business',
      desc: 'Whether you run a pharmacy, restaurant or market stall, EmergencyPOS adapts to your business.',
      items: [
        { label: 'Pharmacies', desc: 'Terminals often fail during peak hours' },
        { label: 'Restaurants', desc: 'Permanent QR on every table' },
        { label: 'Retail stores', desc: 'Backup solution always ready' },
        { label: 'Markets', desc: 'Permanent QR, no internet needed' },
        { label: 'Ride-hailing', desc: 'QR in the vehicle, instant payment' },
        { label: 'Pop-up shops', desc: 'Accept payments without any hardware' },
      ],
    },
    pricing: {
      label: 'Pricing',
      h2: 'Only pay when you get paid',
      desc: 'No subscription. No setup fee. No commitment. Just a small commission on each successful transaction.',
      per: 'per successful transaction',
      min: 'minimum 0.10 EUR per transaction',
      features: ['Unlimited transactions', 'Unlimited cashier accounts', 'Full history', 'Dynamic + permanent QR', 'Apple Pay & Google Pay', 'Real-time confirmations', 'Customer display mode', 'Analytics dashboard', 'Custom email receipts', 'Custom branding', 'Email notifications', 'CSV export'],
      cta: 'Create my free account',
      noCc: 'No credit card required to sign up',
      stripeTitle: 'Stripe fees',
      stripeDesc: 'Stripe charges its own processing fees (typically 1.5% + 0.25 EUR for European cards). These are billed directly by Stripe and are separate from EmergencyPOS fees.',
      zeroSetup: 'Setup fee', zeroMonthly: 'Monthly fee', zeroCommit: 'Commitment', none: 'None',
    },
    security: {
      label: 'Security',
      h2: 'Enterprise-grade payment security',
      items: [
        { title: 'PCI DSS Level 1', desc: 'All payments go through Stripe, a PCI Level 1 certified provider. No card data touches our servers.' },
        { title: 'Direct funds', desc: 'Thanks to Stripe Connect, money goes directly from customer to your bank account. EmergencyPOS never holds your funds.' },
        { title: 'Full traceability', desc: 'Every transaction is timestamped, assigned to the right cashier, with real-time notifications and exportable history.' },
      ],
    },
    cta: {
      h2: 'Never lose a sale again',
      desc: 'Set up EmergencyPOS in 5 minutes. No hardware needed. Ready to accept payments today.',
      btn: 'Create my free account',
      loginBtn: 'Log in',
      sub: 'Free sign-up · No credit card · No commitment',
    },
    footer: {
      desc: 'The emergency QR payment solution for stores. Accept payments even when everything goes down.',
      product: 'Product', securityF: 'Security', account: 'Account',
      pci: 'PCI DSS', stripeConnect: 'Stripe Connect', dataProtection: 'Data protection',
      loginF: 'Login', registerF: 'Sign up',
      copyright: 'All rights reserved.',
      poweredBy: 'Powered by Stripe',
    },
  },
};

/* ── Animated number counter ── */
function AnimatedCounter({ end, suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const counted = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !counted.current) {
        counted.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          setCount(Math.floor(progress * end));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);
  return <span ref={ref} style={{ fontFamily: 'JetBrains Mono, monospace' }}>{count}{suffix}</span>;
}

function SectionLabel({ children }) {
  return (
    <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 px-4 py-1.5 text-xs uppercase tracking-widest text-orange-400 font-bold mb-4">
      <Zap className="w-3 h-3" />{children}
    </div>
  );
}

/* ── CSS Mockups ── */
function MockupKeypad({ t }) {
  return (
    <div className="bg-[#0c0c0f] border border-zinc-800 p-5 w-full">
      <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-bold mb-3">{t.mockup.amount}</div>
      <div className="text-center mb-3">
        <div className="text-3xl font-extrabold text-orange-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>27.50</div>
        <div className="text-[10px] text-zinc-600 mt-0.5">EUR</div>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {[1,2,3,4,5,6,7,8,9,'.',0,'C'].map(k => (
          <div key={k} className="bg-zinc-800/80 text-zinc-400 h-7 flex items-center justify-center text-xs font-medium">{k}</div>
        ))}
      </div>
      <div className="mt-2 bg-orange-500 text-white text-xs font-bold py-2 text-center">{t.mockup.charge}</div>
    </div>
  );
}

function MockupQR({ t }) {
  return (
    <div className="bg-[#0c0c0f] border border-zinc-800 p-5 w-full">
      <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-bold mb-3">{t.mockup.qr}</div>
      <div className="text-center">
        <div className="w-28 h-28 mx-auto bg-white p-2 mb-3">
          <div className="w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YzZjRmNiIvPjxyZWN0IHg9IjEwIiB5PSIxMCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBmaWxsPSIjMDAwIi8+PHJlY3QgeD0iMzUiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiMwMDAiLz48cmVjdCB4PSI1MCIgeT0iMTAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iIzAwMCIvPjxyZWN0IHg9IjcwIiB5PSIxMCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBmaWxsPSIjMDAwIi8+PHJlY3QgeD0iMTAiIHk9IjM1IiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiMwMDAiLz48cmVjdCB4PSIzNSIgeT0iMzUiIHdpZHRoPSIzMCIgaGVpZ2h0PSIzMCIgZmlsbD0iIzAwMCIvPjxyZWN0IHg9IjgwIiB5PSIzNSIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMDAwIi8+PHJlY3QgeD0iMTAiIHk9IjcwIiB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIGZpbGw9IiMwMDAiLz48cmVjdCB4PSI3MCIgeT0iNzAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0iIzAwMCIvPjwvc3ZnPg==')] bg-contain" />
        </div>
        <div className="text-xs text-zinc-500 mb-1">{t.mockup.qrAmount} : 27.50 EUR</div>
        <div className="text-[10px] text-zinc-600">{t.mockup.qrScan}</div>
      </div>
    </div>
  );
}

function MockupConfirmation({ t }) {
  return (
    <div className="bg-[#0c0c0f] border border-zinc-800 p-5 w-full">
      <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-bold mb-3">{t.mockup.confirm}</div>
      <div className="text-center py-2">
        <div className="w-12 h-12 mx-auto bg-emerald-500/10 flex items-center justify-center mb-3"><CheckCircle className="w-8 h-8 text-emerald-500" /></div>
        <div className="text-sm font-bold text-emerald-400 mb-1">{t.mockup.paid}</div>
        <div className="text-2xl font-extrabold mb-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>27.50 EUR</div>
        <div className="text-[10px] text-zinc-600 mt-1">14:32 · Apple Pay</div>
      </div>
    </div>
  );
}

function MockupDashboard({ t }) {
  return (
    <div className="bg-[#0c0c0f] border border-zinc-800 p-4 w-full">
      <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-bold mb-3">{t.mockupDash.title}</div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[{ label: t.mockupDash.today, val: '1 247 EUR' }, { label: t.mockupDash.txns, val: '34' }, { label: t.mockupDash.avg, val: '36.68 EUR' }].map((s, i) => (
          <div key={i} className="bg-zinc-900 p-2"><div className="text-[9px] text-zinc-600 uppercase">{s.label}</div><div className="text-sm font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s.val}</div></div>
        ))}
      </div>
      <div className="flex items-end gap-1 h-12">
        {[40,65,45,80,55,90,70,60,85,75,95,50].map((h, i) => (
          <div key={i} className="flex-1 bg-orange-500/30 hover:bg-orange-500/50 transition-colors" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}

function MockupTransactions({ t }) {
  const txns = [
    { time: '14:32', id: '#a8f3', amount: '27.50', method: 'Apple Pay' },
    { time: '14:18', id: '#b2c1', amount: '15.00', method: 'Card' },
    { time: '13:45', id: '#c9d4', amount: '42.80', method: 'Google Pay' },
  ];
  return (
    <div className="bg-[#0c0c0f] border border-zinc-800 p-4 w-full">
      <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-bold mb-3">{t.mockupTxns.title}</div>
      <div className="space-y-2">
        {txns.map((tx, i) => (
          <div key={i} className="flex items-center justify-between bg-zinc-900 px-3 py-2">
            <div><div className="text-xs text-white font-medium">Paiement {tx.id}</div><div className="text-[9px] text-zinc-600">{tx.time} · {tx.method}</div></div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{tx.amount} EUR</span>
              <span className="text-[8px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 font-bold uppercase">Paid</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockupStaff({ t }) {
  const members = [{ name: 'Marie Dupont', role: 'owner', init: 'MD' }, { name: 'Lucas Martin', role: 'admin', init: 'LM' }, { name: 'Sarah Chen', role: 'cashier', init: 'SC' }];
  return (
    <div className="bg-[#0c0c0f] border border-zinc-800 p-4 w-full">
      <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-bold mb-3">{t.mockupStaff.title}</div>
      <div className="space-y-2">
        {members.map((m, i) => (
          <div key={i} className="flex items-center justify-between bg-zinc-900 px-3 py-2">
            <div className="flex items-center gap-2"><div className="w-6 h-6 bg-orange-500/20 flex items-center justify-center text-[9px] font-bold text-orange-400">{m.init}</div><div className="text-xs text-white font-medium">{m.name}</div></div>
            <span className={`text-[8px] px-1.5 py-0.5 font-bold uppercase ${m.role === 'owner' ? 'bg-orange-500/20 text-orange-400' : m.role === 'admin' ? 'bg-blue-500/20 text-blue-400' : 'bg-zinc-700 text-zinc-400'}`}>{m.role}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const TEAM_ICONS = [UserPlus, QrCode, Eye, Lock];
const STEP_ICONS = [UserPlus, CreditCard, QrCode, CheckCircle];
const UC_ICONS = [Building2, UtensilsCrossed, ShoppingBag, Store, Car, Tent];
const SECURITY_ICONS = [Shield, Lock, Clock];
const PROBLEM_ICONS = [WifiOff, AlertTriangle, CreditCard];
const FEAT_ICONS = [CreditCard, QrCode, QrCode, Smartphone, Users, BarChart3, Receipt, Bell, Download, Globe, Palette, HeadphonesIcon, Eye, TrendingUp, MonitorSmartphone];

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('epos_lang') || 'fr'; } catch { return 'fr'; }
  });

  const t = i18n[lang];

  const toggleLang = () => {
    const next = lang === 'fr' ? 'en' : 'fr';
    setLang(next);
    try { localStorage.setItem('epos_lang', next); } catch {}
  };

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="landing-page">
      {/* NAV */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#09090b]/95 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'}`} data-testid="landing-nav">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2"><Zap className="w-6 h-6 text-orange-500" /><span className="text-lg font-extrabold tracking-tighter" style={{ fontFamily: 'Manrope, sans-serif' }}>EmergencyPOS</span></div>
            <div className="hidden md:flex items-center gap-8">
              {[{ label: t.nav.solution, href: '#solution' }, { label: t.nav.features, href: '#features' }, { label: t.nav.pricing, href: '#pricing' }, { label: t.nav.security, href: '#security' }].map(l => (
                <a key={l.href} href={l.href} className="text-sm text-zinc-400 hover:text-white transition-colors font-medium">{l.label}</a>
              ))}
            </div>
            <div className="hidden md:flex items-center gap-3">
              <button onClick={toggleLang} className="text-xs font-bold text-zinc-400 hover:text-white px-2 py-1 border border-zinc-700 hover:border-zinc-500 transition-colors" data-testid="lang-toggle">
                <Globe className="w-3 h-3 inline mr-1" />{lang === 'fr' ? 'EN' : 'FR'}
              </button>
              <Button variant="ghost" onClick={() => navigate('/login')} className="text-zinc-300 hover:text-white hover:bg-white/5 font-medium" data-testid="nav-login-btn">{t.nav.login}</Button>
              <Button onClick={() => navigate('/login?tab=register')} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold h-9 px-5" data-testid="nav-register-btn">{t.nav.start}</Button>
            </div>
            <button className="md:hidden text-zinc-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-nav-toggle">{mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#09090b] border-t border-white/5 px-4 py-4 space-y-3">
            {[{ label: t.nav.solution, href: '#solution' }, { label: t.nav.features, href: '#features' }, { label: t.nav.pricing, href: '#pricing' }, { label: t.nav.security, href: '#security' }].map(l => (
              <a key={l.href} href={l.href} onClick={() => setMobileMenuOpen(false)} className="block text-sm text-zinc-400 hover:text-white py-2">{l.label}</a>
            ))}
            <button onClick={toggleLang} className="block text-sm text-orange-400 font-bold py-2"><Globe className="w-3.5 h-3.5 inline mr-1" />{lang === 'fr' ? 'English' : 'Français'}</button>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => navigate('/login')} className="flex-1 border-zinc-700 text-zinc-300 hover:bg-white/5">{t.nav.login}</Button>
              <Button onClick={() => navigate('/login?tab=register')} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white">{t.nav.start}</Button>
            </div>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="pt-28 pb-8 md:pt-36 md:pb-12 px-4 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-orange-500/5 blur-[120px] pointer-events-none" />
        <div className="max-w-6xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 px-3 py-1.5 mb-6 text-xs text-red-400 font-bold uppercase tracking-wider"><WifiOff className="w-3.5 h-3.5" />{t.hero.badge}</div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.hero.h1a}<br /><span className="text-orange-500">{t.hero.h1b}</span></h1>
              <p className="text-base sm:text-lg text-zinc-400 max-w-xl mb-8 leading-relaxed">{t.hero.desc}</p>
              <div className="flex flex-col sm:flex-row items-start gap-3">
                <Button onClick={() => navigate('/login?tab=register')} className="h-13 px-8 bg-orange-500 hover:bg-orange-600 text-white font-bold text-base w-full sm:w-auto" data-testid="hero-cta-btn">{t.hero.cta} <ArrowRight className="w-4 h-4 ml-2" /></Button>
                <Button variant="outline" onClick={() => document.getElementById('solution')?.scrollIntoView({ behavior: 'smooth' })} className="h-13 px-8 border-zinc-700 text-zinc-300 hover:bg-white/5 font-medium text-base w-full sm:w-auto" data-testid="hero-demo-btn">{t.hero.demo} <Play className="w-4 h-4 ml-2" /></Button>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm text-zinc-500">
                {[t.hero.noSub, t.hero.noCommit, t.hero.noHw].map((s, i) => <span key={i} className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-emerald-500" />{s}</span>)}
              </div>
            </div>
            <div className="relative hidden lg:block">
              <img src={HERO_IMG} alt="EmergencyPOS" className="w-full h-auto object-cover border border-zinc-800 opacity-90" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-transparent" />
              <div className="absolute -bottom-4 -left-4 bg-[#0c0c0f] border border-zinc-700 p-3 shadow-2xl shadow-black/50" style={{ animation: 'fadeInUp 0.6s ease-out 0.5s both' }}>
                <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-500" /><div><div className="text-xs font-bold text-emerald-400">{t.hero.confirmLabel}</div><div className="text-sm font-extrabold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>27.50 EUR</div></div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-8 px-4 border-y border-zinc-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[{ value: <AnimatedCounter end={99} suffix="%" />, label: t.stats.uptime }, { value: <><AnimatedCounter end={3} /> sec</>, label: t.stats.speed }, { value: <AnimatedCounter end={0} suffix=" EUR" />, label: t.stats.setup }, { value: 'Stripe', label: t.stats.secure }].map((s, i) => (
              <div key={i}><div className="text-2xl sm:text-3xl font-extrabold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s.value}</div><div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider font-medium">{s.label}</div></div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-16 md:py-24 px-4" data-testid="problem-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14"><SectionLabel>{t.problem.label}</SectionLabel><h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.problem.h2}</h2><p className="text-zinc-400 mt-4 max-w-2xl mx-auto text-base leading-relaxed">{t.problem.desc}</p></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {t.problem.items.map((item, i) => { const Icon = PROBLEM_ICONS[i]; return (
              <div key={i} className="bg-zinc-900/50 border border-zinc-800 p-6 md:p-8 hover:border-red-500/20 transition-colors">
                <div className="w-10 h-10 bg-red-500/10 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-red-400" /></div>
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed mb-4">{item.desc}</p>
                <div className="border-t border-zinc-800 pt-4"><div className="text-xl font-extrabold text-red-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{item.stat}</div><div className="text-xs text-zinc-600 mt-0.5">{item.statLabel}</div></div>
              </div>
            ); })}
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section id="solution" className="py-16 md:py-24 px-4 bg-zinc-900/30" data-testid="solution-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14"><SectionLabel>{t.solution.label}</SectionLabel><h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.solution.h2}</h2><p className="text-zinc-400 mt-4 max-w-2xl mx-auto text-base leading-relaxed">{t.solution.desc}</p></div>
          <div className="bg-zinc-900/50 border border-zinc-800 p-4 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[MockupKeypad, MockupQR, MockupConfirmation].map((C, i) => (
                <div key={i} className="relative flex items-center">
                  {i > 0 && <ChevronRight className="hidden md:block absolute -left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-500/50 z-10" />}
                  <div className="w-full"><div className="absolute -top-2 -left-2 w-6 h-6 bg-orange-500 flex items-center justify-center text-xs font-bold text-white z-10">{i + 1}</div><C t={t} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* QR MODES */}
      <section className="py-16 md:py-24 px-4" data-testid="qr-modes-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14"><SectionLabel>{t.qrModes.label}</SectionLabel><h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.qrModes.h2}</h2><p className="text-zinc-400 mt-4 max-w-2xl mx-auto text-base leading-relaxed">{t.qrModes.desc}</p></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Dynamic */}
            <div className="bg-zinc-900/50 border border-zinc-800 p-6 md:p-8 hover:border-orange-500/30 transition-colors">
              <div className="flex items-center gap-3 mb-6"><div className="w-12 h-12 bg-orange-500/10 flex items-center justify-center"><Smartphone className="w-6 h-6 text-orange-500" /></div><div><h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.qrModes.dynamic.title}</h3><span className="text-xs text-zinc-500">{t.qrModes.dynamic.sub}</span></div></div>
              <p className="text-sm text-zinc-400 leading-relaxed mb-6">{t.qrModes.dynamic.desc}</p>
              <div className="space-y-2">{t.qrModes.dynamic.features.map((f, i) => <div key={i} className="flex items-center gap-2 text-sm text-zinc-300"><CheckCircle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />{f}</div>)}</div>
              <div className="mt-6 border-t border-zinc-800 pt-4"><div className="text-xs text-zinc-600 uppercase tracking-wider font-bold mb-2">{t.qrModes.dynamic.idealFor}</div><div className="flex flex-wrap gap-2">{t.qrModes.dynamic.cases.map((u, i) => <span key={i} className="text-xs bg-zinc-800 text-zinc-400 px-2 py-1">{u}</span>)}</div></div>
            </div>
            {/* Permanent */}
            <div className="bg-zinc-900/50 border border-zinc-800 p-6 md:p-8 hover:border-orange-500/30 transition-colors relative overflow-hidden">
              <div className="absolute top-3 right-3 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">{t.qrModes.permanent.badge}</div>
              <div className="flex items-center gap-3 mb-6"><div className="w-12 h-12 bg-orange-500/10 flex items-center justify-center"><QrCode className="w-6 h-6 text-orange-500" /></div><div><h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.qrModes.permanent.title}</h3><span className="text-xs text-zinc-500">{t.qrModes.permanent.sub}</span></div></div>
              <p className="text-sm text-zinc-400 leading-relaxed mb-6">{t.qrModes.permanent.desc}</p>
              <div className="space-y-2">{t.qrModes.permanent.features.map((f, i) => <div key={i} className="flex items-center gap-2 text-sm text-zinc-300"><CheckCircle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />{f}</div>)}</div>
              <div className="mt-6 border-t border-zinc-800 pt-4"><div className="text-xs text-zinc-600 uppercase tracking-wider font-bold mb-2">{t.qrModes.permanent.idealFor}</div><div className="flex flex-wrap gap-2">{t.qrModes.permanent.cases.map((u, i) => <span key={i} className="text-xs bg-zinc-800 text-zinc-400 px-2 py-1">{u}</span>)}</div></div>
            </div>
          </div>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
            <div className="relative"><img src={QR_COUNTER_IMG} alt="QR permanent" className="w-full h-64 md:h-80 object-cover border border-zinc-800 opacity-80" loading="lazy" /><div className="absolute inset-0 bg-gradient-to-r from-[#09090b]/80 via-transparent to-transparent" /></div>
            <div className="space-y-4"><h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.qrModes.imgTitle}</h3><p className="text-sm text-zinc-400 leading-relaxed">{t.qrModes.imgP1}</p><p className="text-sm text-zinc-400 leading-relaxed">{t.qrModes.imgP2}</p></div>
          </div>
        </div>
      </section>

      {/* PLATFORM */}
      <section id="features" className="py-16 md:py-24 px-4 bg-zinc-900/30" data-testid="platform-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14"><SectionLabel>{t.platform.label}</SectionLabel><h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.platform.h2}</h2><p className="text-zinc-400 mt-4 max-w-2xl mx-auto text-base leading-relaxed">{t.platform.desc}</p></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2"><MockupDashboard t={t} /></div>
            <MockupTransactions t={t} />
            <MockupStaff t={t} />
            <div className="lg:col-span-2">
              <div className="bg-[#0c0c0f] border border-zinc-800 p-4">
                <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-bold mb-3">{t.platform.allFeatures}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {t.platform.featureList.map((label, i) => { const Icon = FEAT_ICONS[i]; return (
                    <div key={i} className="flex items-center gap-2 bg-zinc-900 px-3 py-2.5"><Icon className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" /><span className="text-xs text-zinc-300 font-medium">{label}</span></div>
                  ); })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-16 md:py-24 px-4" data-testid="team-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionLabel>{t.team.label}</SectionLabel>
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.team.h2}</h2>
              <p className="text-zinc-400 text-base leading-relaxed mb-6">{t.team.desc}</p>
              <div className="space-y-4">
                {t.team.items.map((item, i) => { const Icon = TEAM_ICONS[i]; return (
                  <div key={i} className="flex items-start gap-3"><div className="w-8 h-8 bg-orange-500/10 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon className="w-4 h-4 text-orange-500" /></div><div><div className="text-sm font-bold text-white">{item.title}</div><div className="text-xs text-zinc-500 leading-relaxed">{item.desc}</div></div></div>
                ); })}
              </div>
            </div>
            <div className="relative"><img src={TEAM_IMG} alt="Team" className="w-full h-auto object-cover border border-zinc-800 opacity-80" loading="lazy" /><div className="absolute inset-0 bg-gradient-to-l from-[#09090b]/60 via-transparent to-transparent" /></div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 md:py-24 px-4 bg-zinc-900/30" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14"><SectionLabel>{t.howItWorks.label}</SectionLabel><h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.howItWorks.h2}</h2></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.howItWorks.steps.map((step, i) => { const Icon = STEP_ICONS[i]; return (
              <div key={i} className="relative group"><div className="bg-zinc-900 border border-zinc-800 p-6 hover:border-orange-500/30 transition-colors h-full"><div className="text-xs font-bold text-orange-500 mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>0{i + 1}</div><div className="w-10 h-10 bg-orange-500/10 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-orange-500" /></div><h3 className="font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{step.title}</h3><p className="text-sm text-zinc-400 leading-relaxed">{step.desc}</p></div>{i < 3 && <ChevronRight className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-700" />}</div>
            ); })}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-16 md:py-24 px-4" data-testid="use-cases-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14"><SectionLabel>{t.useCases.label}</SectionLabel><h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.useCases.h2}</h2><p className="text-zinc-400 mt-4 max-w-2xl mx-auto text-base leading-relaxed">{t.useCases.desc}</p></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {t.useCases.items.map((uc, i) => { const Icon = UC_ICONS[i]; return (
              <div key={i} className="bg-zinc-900 border border-zinc-800 p-4 text-center hover:border-orange-500/30 transition-colors group"><Icon className="w-7 h-7 text-orange-500 mx-auto mb-2 group-hover:scale-110 transition-transform" /><div className="text-sm font-bold text-zinc-200 mb-1">{uc.label}</div><div className="text-[10px] text-zinc-600 leading-snug">{uc.desc}</div></div>
            ); })}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-16 md:py-24 px-4 bg-zinc-900/30" data-testid="pricing-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14"><SectionLabel>{t.pricing.label}</SectionLabel><h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.pricing.h2}</h2><p className="text-zinc-400 mt-4 max-w-xl mx-auto text-base leading-relaxed">{t.pricing.desc}</p></div>
          <div className="max-w-lg mx-auto">
            <div className="bg-zinc-900 border-2 border-orange-500 p-8 text-center" data-testid="pricing-main">
              <div className="flex items-baseline justify-center gap-1 mb-2"><span className="text-6xl font-extrabold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>0.99</span><span className="text-2xl font-bold text-orange-500">%</span></div>
              <p className="text-zinc-400 text-sm mb-1">{t.pricing.per}</p><p className="text-zinc-600 text-xs mb-8">{t.pricing.min}</p>
              <div className="grid grid-cols-2 gap-3 mb-8 text-left">{t.pricing.features.map((f, j) => <div key={j} className="flex items-center gap-2 text-sm text-zinc-300"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />{f}</div>)}</div>
              <Button onClick={() => navigate('/login?tab=register')} className="w-full h-13 bg-orange-500 hover:bg-orange-600 text-white font-bold text-base" data-testid="pricing-cta-btn">{t.pricing.cta} <ArrowRight className="w-4 h-4 ml-2" /></Button>
              <p className="text-xs text-zinc-600 mt-3">{t.pricing.noCc}</p>
            </div>
            <div className="mt-6 bg-zinc-900/50 border border-zinc-800 p-5" data-testid="pricing-stripe-notice"><div className="flex items-start gap-3"><Shield className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm text-zinc-300 font-medium mb-1">{t.pricing.stripeTitle}</p><p className="text-xs text-zinc-500 leading-relaxed">{t.pricing.stripeDesc}</p></div></div></div>
            <div className="grid grid-cols-3 gap-px mt-6 bg-zinc-800">
              {[{ label: t.pricing.zeroSetup, value: '0 EUR' }, { label: t.pricing.zeroMonthly, value: '0 EUR' }, { label: t.pricing.zeroCommit, value: t.pricing.none }].map((item, i) => (
                <div key={i} className="bg-zinc-900 p-4 text-center"><div className="text-lg font-extrabold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{item.value}</div><div className="text-xs text-zinc-500 mt-1">{item.label}</div></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section id="security" className="py-16 md:py-24 px-4" data-testid="security-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14"><SectionLabel>{t.security.label}</SectionLabel><h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.security.h2}</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {t.security.items.map((item, i) => { const Icon = SECURITY_ICONS[i]; return (
              <div key={i} className="bg-zinc-900/50 border border-zinc-800 p-6"><div className="w-10 h-10 bg-orange-500/10 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-orange-500" /></div><h3 className="font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.title}</h3><p className="text-sm text-zinc-400 leading-relaxed">{item.desc}</p></div>
            ); })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 px-4 bg-zinc-900/30" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.cta.h2}</h2>
          <p className="text-zinc-400 mb-8 text-base max-w-xl mx-auto leading-relaxed">{t.cta.desc}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button onClick={() => navigate('/login?tab=register')} className="h-13 px-10 bg-orange-500 hover:bg-orange-600 text-white font-bold text-base w-full sm:w-auto" data-testid="cta-register-btn">{t.cta.btn} <ArrowRight className="w-4 h-4 ml-2" /></Button>
            <Button variant="outline" onClick={() => navigate('/login')} className="h-13 px-8 border-zinc-700 text-zinc-300 hover:bg-white/5 font-medium text-base w-full sm:w-auto">{t.cta.loginBtn}</Button>
          </div>
          <p className="text-xs text-zinc-600 mt-4">{t.cta.sub}</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-zinc-800 py-10 px-4" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div><div className="flex items-center gap-2 mb-3"><Zap className="w-5 h-5 text-orange-500" /><span className="text-sm font-extrabold tracking-tighter" style={{ fontFamily: 'Manrope, sans-serif' }}>EmergencyPOS</span></div><p className="text-xs text-zinc-600 leading-relaxed">{t.footer.desc}</p></div>
            <div><div className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3">{t.footer.product}</div><div className="space-y-2"><a href="#solution" className="block text-sm text-zinc-500 hover:text-zinc-300 transition-colors">{t.nav.solution}</a><a href="#features" className="block text-sm text-zinc-500 hover:text-zinc-300 transition-colors">{t.nav.features}</a><a href="#pricing" className="block text-sm text-zinc-500 hover:text-zinc-300 transition-colors">{t.nav.pricing}</a></div></div>
            <div><div className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3">{t.footer.securityF}</div><div className="space-y-2"><a href="#security" className="block text-sm text-zinc-500 hover:text-zinc-300 transition-colors">{t.footer.pci}</a><a href="#security" className="block text-sm text-zinc-500 hover:text-zinc-300 transition-colors">{t.footer.stripeConnect}</a><a href="#security" className="block text-sm text-zinc-500 hover:text-zinc-300 transition-colors">{t.footer.dataProtection}</a></div></div>
            <div><div className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3">{t.footer.account}</div><div className="space-y-2"><button onClick={() => navigate('/login')} className="block text-sm text-zinc-500 hover:text-zinc-300 transition-colors">{t.footer.loginF}</button><button onClick={() => navigate('/login?tab=register')} className="block text-sm text-zinc-500 hover:text-zinc-300 transition-colors">{t.footer.registerF}</button></div></div>
          </div>
          <div className="border-t border-zinc-800 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-xs text-zinc-600">&copy; {new Date().getFullYear()} EmergencyPOS. {t.footer.copyright}</div>
            <div className="flex items-center gap-4 text-xs text-zinc-600"><span>{t.footer.poweredBy}</span></div>
          </div>
        </div>
      </footer>

      <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
